"use client";
/**
 * Bridge between the pure BattleEngine and the zustand game-store + audio engine.
 *
 * The engine emits `BattleEvent`s; this module subscribes to them and:
 *  - mirrors UI state into the store (log, opponent HP, phase, outcome)
 *  - plays the appropriate retro SFX via the audio engine
 *
 * SSR-safe: all exports tolerate `typeof window === 'undefined'` and return
 * benign defaults rather than touching the store or audio engine.
 */

import {
  BattleEngine,
  type BattleEvent,
  type BattleParticipant,
} from '@/engine/battle/BattleEngine';
import { useGameStore } from '@/stores/game-store';
import type { StatusEffect, StatusKind } from '@/stores/game-store';
import { playSfx } from '@/engine/audio';
import {
  emitJuice,
  haptic,
  shakeFromHit,
  cameraPunch,
  requestHitStop,
} from '@/engine/juice';

function isBrowser(): boolean {
  return typeof window !== 'undefined';
}

/**
 * Tracks player HP across `damage` events so callers can read current player
 * HP without rummaging through engine internals. The engine does not expose
 * a player HP setter in the store yet — we surface it via the returned ref.
 */
export interface PlayerHpTracker {
  current: number;
  max: number;
}

/**
 * Construct a BattleEngine wired up to the game-store and audio engine.
 *
 * The caller drives the battle via `engine.selectMove(...)`, `engine.useItem(...)`,
 * `engine.switchSurggie(...)`, and `engine.attemptFlee()`. UI state stays in sync
 * automatically through the `onEvent` handler installed here.
 */
export function createBoundBattleEngine(
  player: BattleParticipant,
  opponent: BattleParticipant,
  opts: { isWild?: boolean } = {},
): BattleEngine {
  // ── Hit-stop / juice pacing state (per-engine, closed over) ──────────────
  // The engine emits a whole turn's events synchronously into this handler,
  // so the `damage` event arrives *before* the "A critical hit!" log that
  // would tell us the hit was a crit. We therefore buffer the juice for a
  // damage event and flush it on a microtask — by then the crit log has run
  // and set `pendingCrit`. Store state (HP, SFX) still updates immediately, so
  // the turn never stalls; only the *feel* layer is paced.
  let pendingCrit = false;
  type PendingHit = { target: 'player' | 'opponent'; amount: number; effectiveness: number };
  let pendingHit: PendingHit | null = null;

  const flushHit = (): void => {
    const hit = pendingHit;
    pendingHit = null;
    const crit = pendingCrit;
    pendingCrit = false;
    if (!hit) return;
    // BattleEffects renders the hit-stop freeze + crit flash off this event.
    emitJuice({
      kind: 'hit',
      target: hit.target,
      amount: hit.amount,
      effectiveness: hit.effectiveness,
      crit,
    });
    // Camera impulses (consumed by BattleArena's panel transform).
    shakeFromHit(hit.amount, hit.effectiveness, crit);
    if (hit.effectiveness > 1 || crit) {
      cameraPunch({ zoom: crit ? 0.07 : 0.05, kickY: crit ? 8 : 6 });
    }
    // Hit-stop: short freeze on a normal hit, longer on crit / super-effective.
    requestHitStop(crit ? 200 : hit.effectiveness > 1 ? 120 : 70);
    haptic(crit ? 'crit' : 'hit');
  };

  const handleEvent = (event: BattleEvent): void => {
    if (!isBrowser()) return;

    const store = useGameStore.getState();

    // Crit detection: the engine logs this line immediately after the damage
    // event for the same hit. Latch it so the buffered juice flush can read it.
    if (event.kind === 'log' && event.text === 'A critical hit!') {
      pendingCrit = true;
    }

    // Status-effect events are part of the shared status contract. The
    // BattleEngine emits { kind:'status', target, status, turns }; we mirror
    // them onto playerStatus / opponentStatus in the store. Read defensively so
    // this compiles whether or not the engine's BattleEvent union already lists
    // the 'status' kind (ENGINE lands the emit side independently).
    const maybeStatus = event as {
      kind: string;
      target?: 'player' | 'opponent';
      status?: StatusKind;
      turns?: number;
    };
    if (maybeStatus.kind === 'status') {
      const next: StatusEffect = {
        kind: maybeStatus.status ?? 'none',
        turns: maybeStatus.turns ?? 0,
      };
      if (maybeStatus.target === 'opponent') {
        store.setOpponentStatus(next);
      } else {
        store.setPlayerStatus(next);
      }
      return;
    }

    switch (event.kind) {
      case 'log': {
        store.appendBattleLog(event.text);
        return;
      }

      case 'damage': {
        if (event.target === 'opponent') {
          const nextHp = Math.max(0, store.battle.opponentHpCurrent - event.amount);
          store.setOpponentHp(nextHp);
        } else {
          const nextHp = Math.max(0, store.battle.playerHpCurrent - event.amount);
          store.setPlayerHp(nextHp);
        }
        // Effectiveness-tiered SFX. `effectiveness` may be undefined when the
        // event isn't from a typed attack (e.g. self-damage). Treat as 1.
        const eff = event.effectiveness ?? 1;
        if (eff > 1) {
          playSfx('battle_super');
        } else if (eff < 1) {
          playSfx('battle_weak');
        } else {
          playSfx('battle_hit');
        }

        // Buffer juice for this hit and flush on a microtask, after the
        // (synchronous) crit log has had a chance to latch `pendingCrit`.
        // Only landed damage gets feel feedback — a 0-damage "no effect" hit
        // shouldn't shake the screen.
        if (event.amount > 0) {
          // Flush any prior un-flushed hit defensively (shouldn't happen, but
          // keeps one hit == one juice beat even if events interleave).
          if (pendingHit) flushHit();
          pendingHit = { target: event.target, amount: event.amount, effectiveness: eff };
          queueMicrotask(flushHit);
        }
        return;
      }

      case 'phase': {
        store.setBattlePhase(event.phase);
        return;
      }

      case 'faint': {
        // Flush a still-buffered hit first so its shake doesn't land *after*
        // the heavier KO kick (ordering reads wrong otherwise).
        if (pendingHit) flushHit();
        if (event.target === 'opponent') {
          store.setOpponentHp(0);
        } else {
          store.setPlayerHp(0);
        }
        playSfx('battle_faint');
        emitJuice({ kind: 'ko', target: event.target });
        // KO gets a bigger, slower camera punch + a settling shake.
        cameraPunch({ zoom: 0.08, kickY: 10, duration: 480 });
        shakeFromHit(40, 1, false);
        haptic('ko');
        return;
      }

      case 'caught': {
        playSfx('catch_success');
        emitJuice({ kind: 'catch' });
        haptic('catch');
        return;
      }

      case 'fled': {
        playSfx('menu_back');
        return;
      }

      case 'end': {
        if (event.outcome === 'victory' || event.outcome === 'caught') {
          playSfx('battle_win');
          emitJuice({ kind: 'victory' });
        } else if (event.outcome === 'defeat') {
          playSfx('battle_lose');
          emitJuice({ kind: 'defeat' });
        }
        // Defer the store-level teardown so the KO faint / catch-shake
        // animation can paint before <BattleArena> unmounts (the arena is
        // gated on battle.active). The engine already set the terminal phase
        // (victory/defeat/caught/fled) synchronously a tick earlier, so the
        // menu is locked (canUseMenu is false for these phases) during the
        // beat — no input leaks. `fled` has nothing to animate, so it ends at
        // once. The post-battle HP/XP sync runs when active flips, i.e. after
        // the animation, reading the engine's unchanged final state.
        const outcome = event.outcome;
        if (outcome === 'fled') {
          store.endBattle(outcome);
        } else {
          const beatMs = outcome === 'caught' ? 1300 : 1000;
          window.setTimeout(() => {
            useGameStore.getState().endBattle(outcome);
          }, beatMs);
        }
        return;
      }
    }
  };

  // Seed player HP into the store at battle start. The engine emits no
  // start/init event (only log/damage/phase/faint/caught/fled/end), but the
  // player participant carries authoritative hpCurrent/hpMax here, so we mirror
  // it now — keeping the static-player-HP bug from re-surfacing in the UI.
  if (isBrowser()) {
    useGameStore.getState().setPlayerHp(player.hpCurrent, player.hpMax);
  }

  return new BattleEngine(player, opponent, {
    isWild: opts.isWild ?? true,
    onEvent: handleEvent,
  });
}

/**
 * Action descriptor accepted by `dispatchBattleAction`.
 */
export type BattleAction =
  | { kind: 'move'; moveId: string }
  | { kind: 'item'; itemId: string }
  | { kind: 'switch'; replacement: BattleParticipant }
  | { kind: 'flee' };

/**
 * Dispatch a single battle action against the engine.
 *
 * Resolves once the engine has finished processing the action's turn cycle.
 * The bridge's `onEvent` handler keeps store state in sync for the duration.
 * On SSR (no window), resolves immediately without touching the engine.
 */
export async function dispatchBattleAction(
  engine: BattleEngine,
  action: BattleAction,
): Promise<void> {
  if (!isBrowser()) return;

  switch (action.kind) {
    case 'move':
      await engine.selectMove(action.moveId);
      return;
    case 'item':
      await engine.useItem(action.itemId);
      return;
    case 'switch':
      engine.switchSurggie(action.replacement);
      return;
    case 'flee':
      await engine.attemptFlee();
      return;
  }
}
